import React, { useState, useMemo, type JSX } from 'react';
import EDView from '../../BaseComponents/View/EDView';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import VyValjare from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/VyValjare/VyValjare';
import EDGrid from '../../BaseComponents/GridComponents/EDGrid';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import { SideBySideWidthType } from '../../Utils/ObjektVyUtils';
import { contains } from 'ramda';
import { useIsLargeScreen } from '../../Hooks/useFullscreenView';

interface IProps<VyPropsType> {
  caption?: string;
  vyProps: VyPropsType;
  readonly vyer: Array<{
    caption: string;
    comp: React.ComponentType<React.PropsWithChildren<VyPropsType>>;
  }>;
  sidaVidSida?: boolean;
  sideBySideWidths?: SideBySideWidthType[];
  tabbed: boolean;
  classes?: any;
}

const styles = (theme: Theme) => ({
  container: {
    padding: '32px 96px 12px 96px',
    [theme.breakpoints.down('lg')]: {
      padding: '32px 24px 12px 24px',
    },
    position: 'fixed',
    top: '152px',
    left: '0px',
    right: '0px',
    bottom: '0px',
    overflowY: 'hidden',
  },
});

const getSideBySideWidth = (
  sideBySideWidths: SideBySideWidthType[] | undefined,
  vyIndex: number
) =>
  sideBySideWidths != null && sideBySideWidths[vyIndex] != null
    ? sideBySideWidths[vyIndex]
    : true;

const MultiVyBase = <T extends Record<string, unknown>>(props: IProps<T>) => {
  const largeScreen = useIsLargeScreen();
  const [aktuellaVyer, setAktuellaVyer] = useState(
    props.sidaVidSida && largeScreen ? [0, 1] : [0]
  );
  const vyer = useMemo(() => {
    const sidor = props.vyer.map((vy, idx) => ({
      aktiv: contains(idx, aktuellaVyer),
      collectionName: '',
      hidden: false,
      ...vy,
    }));
    return sidor;
  }, [props.vyer, aktuellaVyer]);
  const AktuellaVyComps: JSX.Element =
    aktuellaVyer.length === 1 ? (
      React.createElement(props.vyer[aktuellaVyer[0]].comp, {
        config: { fullPage: true },
        ...props.vyProps,
      })
    ) : (
      <EDView className={props.classes.container}>
        <EDGrid
          container
          spacing={4}
          wrap="nowrap"
          style={{ height: '100%', overflowY: 'hidden' }}
        >
          {vyer
            .filter(vy => vy.aktiv)
            .map((vy, idx) => {
              const Comp = vy.comp;
              return (
                <EDGrid
                  key={idx}
                  item
                  style={{ flexGrow: 1, overflowY: 'auto' }}
                  xs={getSideBySideWidth(props.sideBySideWidths, idx)}
                >
                  <Comp key={vy.caption} {...props.vyProps} />
                </EDGrid>
              );
            })}
        </EDGrid>
      </EDView>
    );
  return (
    <EDView
      style={{
        width: '100%',
        display: 'inherit',
        flexDirection: 'inherit',
        flex: 'inherit',
        height: '100%',
      }}
    >
      <EDTypography variant="subtitle1">{props.caption || ''}</EDTypography>

      <VyValjare
        key="valjare"
        setAktuellaVyer={setAktuellaVyer}
        vyer={vyer}
        tabbed={props.tabbed || false}
        sidaVidSida={props.sidaVidSida}
        fullScreen
      />
      {AktuellaVyComps}
    </EDView>
  );
};

export default withStyles(MultiVyBase, styles);
